import { SubstituteRowData, type Player as UIPlayer } from '@dreamteam-monorepo/ui';
import { ContestantPlayer, MatchdayStats, MatchEventsData } from '@/lib/network';
import { PlayerMatch } from '@/lib/network/players';
import type { Player, EventCategory, EventEntry, EventsByCategory, MatchData } from './types';

export const convertPlayersToSubstituteRows = (
  players: Player[],
  onPressPlayer?: (player: Player) => void
): SubstituteRowData[] => {
  return players.map(player => {
    const isSubbedOn = player.events?.subOn ?? false;
    const totalPoints = (player.basicPoints ?? 0) + (player.ppmPoints ?? 0);
    return {
      id: player.id,
      playerName: player.name,
      team: player.team,
      state: isSubbedOn ? 'active' : 'default',
      minute: isSubbedOn ? `${player.events?.subOnMinute ?? 91}'` : undefined,
      points: isSubbedOn ? totalPoints : 0,
      positionLabel: `${player.position || 'SUB'}`,
      position: player.position,
      onPress: onPressPlayer ? () => onPressPlayer(player) : undefined,
    };
  });
};

export const toMinuteLabel = (min: number, _sec: string | null): string => {
  return `${min}'`;
};

export const toEventsByCategory = (
  events: MatchEventsData,
  homeContestantId: string,
  awayContestantId: string
): EventsByCategory[] => {
  const sections: Record<EventCategory, { home: EventEntry[]; away: EventEntry[] }> = {
    goal: { home: [], away: [] },
    ownGoal: { home: [], away: [] },
    assist: { home: [], away: [] },
    yellowCard: { home: [], away: [] },
    redCard: { home: [], away: [] },
    penaltyMiss: { home: [], away: [] },
    penaltySave: { home: [], away: [] },
    subOn: { home: [], away: [] },
    subOff: { home: [], away: [] },
  };

  const pushEvent = (contestantId: string | null, category: EventCategory, entry: EventEntry) => {
    if (contestantId === homeContestantId) sections[category].home.push(entry);
    else if (contestantId === awayContestantId) sections[category].away.push(entry);
  };

  for (const goal of events.goals) {
    const category = goal.isOwnGoal ? 'ownGoal' : 'goal';
    // Own goals count for the opponent, so flip the side
    const effectiveContestantId = goal.isOwnGoal
      ? goal.contestantId === homeContestantId
        ? awayContestantId
        : goal.contestantId === awayContestantId
          ? homeContestantId
          : goal.contestantId
      : goal.contestantId;
    pushEvent(effectiveContestantId, category, {
      name: goal.scorerName || 'Unknown',
      minutes: [toMinuteLabel(goal.timeMin, goal.timeMinSec)],
      isPenalty: goal.isPenalty,
    });
  }

  for (const assist of events.assists) {
    pushEvent(assist.contestantId, 'assist', {
      name: assist.assistPlayerName || 'Unknown',
      minutes: [toMinuteLabel(assist.timeMin, assist.timeMinSec)],
    });
  }

  for (const card of events.cards) {
    if (card.type === 'YC') {
      pushEvent(card.contestantId, 'yellowCard', {
        name: card.playerName || 'Unknown',
        minutes: [toMinuteLabel(card.timeMin, card.timeMinSec)],
      });
    } else if (card.type === 'RC') {
      pushEvent(card.contestantId, 'redCard', {
        name: card.playerName || 'Unknown',
        minutes: [toMinuteLabel(card.timeMin, card.timeMinSec)],
      });
    }
  }

  for (const pen of events.penalties) {
    if (pen.type === 'PM') {
      pushEvent(pen.contestantId, 'penaltyMiss', {
        name: pen.playerName || 'Unknown',
        minutes: [toMinuteLabel(pen.timeMin, pen.timeMinSec)],
      });
    } else if (pen.type === 'PS') {
      pushEvent(pen.contestantId, 'penaltySave', {
        name: pen.playerName || 'Unknown',
        minutes: [toMinuteLabel(pen.timeMin, pen.timeMinSec)],
      });
    }
  }

  for (const sub of events.substitutesOn) {
    pushEvent(sub.contestantId, 'subOn', {
      name: sub.playerName || 'Unknown',
      minutes: [toMinuteLabel(sub.timeMin, sub.timeMinSec)],
    });
  }

  for (const sub of events.substitutesOff) {
    pushEvent(sub.contestantId, 'subOff', {
      name: sub.playerName || 'Unknown',
      minutes: [toMinuteLabel(sub.timeMin, sub.timeMinSec)],
    });
  }

  const categoryOrder: EventCategory[] = [
    'goal',
    'ownGoal',
    'assist',
    'yellowCard',
    'redCard',
    'penaltyMiss',
    'penaltySave',
    'subOn',
    'subOff',
  ];

  return categoryOrder
    .filter(cat => sections[cat].home.length > 0 || sections[cat].away.length > 0)
    .map(cat => ({
      category: cat,
      homeEvents: sections[cat].home,
      awayEvents: sections[cat].away,
    }));
};

export const mapPlayers = (players: ContestantPlayer[], teamCode: string): Player[] =>
  players.map((p, index) => ({
    id: p.personId,
    fantasyPlayerId: p.fantasyPlayerId,
    firstName: p.firstName,
    lastName: p.lastName,
    name: p.displayName || 'Unknown',
    number: index + 1,
    position: p.position ?? undefined,
    team: teamCode,
    playsInCurrentGameweek: p.playsInCurrentGameweek,
    basicPoints: p.basicPoints,
    ppmPoints: p.ppmPoints,
    breakdown: p.breakdown,
    price: p.price,
    priceChange: p.priceChange,
    events: {
      goals: p.events.goals.filter(g => !g.isOwnGoal).length,
      ownGoal: p.events.goals.some(g => g.isOwnGoal),
      // Fantasy assists have no Opta assist event; the backend folds them into p.assists.
      assists: Math.max(p.events.assists.length, p.assists ?? 0),
      yellowCard: p.events.cards.some(c => c.type === 'YC'),
      redCard: p.events.cards.some(c => c.type === 'RC'),
      subOn: p.events.substitutesOn.length > 0,
      subOff: p.events.substitutesOff.length > 0,
      subOnMinute: p.events.substitutesOn[0]?.timeMin,
      subOffMinute: p.events.substitutesOff[0]?.timeMin,
      penaltyMiss: p.events.penalties.some(e => e.type === 'PM'),
      penaltySave: p.events.penalties.some(e => e.type === 'PS'),
    },
    stats: {
      goals: p.goals ?? null,
      assists: p.assists ?? null,
      fantasyAssists: p.fantasyAssists ?? null,
      shotsOnTarget: p.shotsOnTarget ?? null,
      chancesCreated: p.chancesCreated ?? null,
      tackles: p.tackles ?? null,
      cleanSheet: p.cleanSheet ?? null,
      saves: p.saves ?? null,
      goalsConceded: p.goalsConceded ?? null,
      yellowCards: p.yellowCards ?? null,
      redCards: p.redCards ?? null,
      ownGoals: p.ownGoals ?? null,
      penaltyMisses: p.penaltyMisses ?? null,
      penaltySaves: p.penaltySaves ?? null,
      dribbles: p.dribbles ?? null,
      crosses: p.crosses ?? null,
      offsides: p.offsides ?? null,
      passCompletionRate: p.passCompletionRate ?? null,
      interceptions: p.interceptions ?? null,
      blocks: p.blocks ?? null,
      foulsWon: p.foulsWon ?? null,
      foulsMade: p.foulsMade ?? null,
      goalsOutsideArea: p.goalsOutsideArea ?? null,
      errorsLeadingToGoal: p.errorsLeadingToGoal ?? null,
      punches: p.punches ?? null,
      claims: p.claims ?? null,
      keeperSweeps: p.keeperSweeps ?? null,
      minutesPlayed: p.minutesPlayed ?? null,
    },
  }));

export const isBeforeLineupsAnnouncement = (kickoffAt: string): boolean => {
  const kickoff = new Date(kickoffAt);
  const now = new Date();
  const lineupsAt = new Date(kickoff.getTime() - 75 * 60 * 1000);
  return now < lineupsAt;
};

export const toModalPlayer = (player: Player): UIPlayer => ({
  id: player.id,
  squadId: player.team,
  displayName: player.name,
  country: player.team,
  contestantFlagKey: player.team,
  clubName: player.team,
  shirtKey: player.shirtKey,
  firstName: player.firstName,
  lastName: player.lastName,
  position: (player.position ?? 'MID') as UIPlayer['position'],
  price: player.price ?? 0,
  priceChange: player.priceChange ?? 0,
  matchdayPoints: player.basicPoints ?? 0,
  gameweekPoints: player.basicPoints ?? 0,
  totalPoints: player.basicPoints ?? 0,
  averagePoints: 0,
  bonusPoints: player.ppmPoints ?? 0,
  last3Average: 0,
  percentSelected: 0,
  status: 'available',
  suspensionDetails: null,
  injuryDetails: null,
  hasLeft: false,
});

export const buildMatchStat = (player: Player, isTotalPoints: boolean = false): MatchdayStats => {
  const b = player.breakdown ?? {};
  const s = player.stats ?? {};

  const matchdayStats: MatchdayStats = {
    stats: {
      goals: s.goals ?? null,
      assists: s.assists ?? null,
      fantasyAssists: s.fantasyAssists ?? null,
      shotsOnTarget: s.shotsOnTarget ?? null,
      chancesCreated: s.chancesCreated ?? null,
      tackles: s.tackles ?? null,
      cleanSheet: s.cleanSheet ?? null,
      saves: s.saves ?? null,
      goalsConceded: s.goalsConceded ?? null,
      yellowCards: s.yellowCards ?? null,
      redCards: s.redCards ?? null,
      ownGoals: s.ownGoals ?? null,
      penaltyMisses: s.penaltyMisses ?? null,
      penaltySaves: s.penaltySaves ?? null,
      bonusPpm: null,
      dribbles: s.dribbles ?? null,
      crosses: s.crosses ?? null,
      offsides: s.offsides ?? null,
      passCompletionRate: s.passCompletionRate ?? null,
      interceptions: s.interceptions ?? null,
      blocks: s.blocks ?? null,
      foulsWon: s.foulsWon ?? null,
      foulsMade: s.foulsMade ?? null,
      goalsOutsideArea: s.goalsOutsideArea ?? null,
      errorsLeadingToGoal: s.errorsLeadingToGoal ?? null,
      punches: s.punches ?? null,
      claims: s.claims ?? null,
      keeperSweeps: s.keeperSweeps ?? null,
      minutesPlayed: s.minutesPlayed ?? null,
    },
    breakdown: {
      appearance: b.appearance,
      goals: b.goals,
      assists: b.assists,
      fantasy_assists: b.fantasy_assists,
      shots_on_target: b.shots_on_target,
      big_chances_created: b.big_chances_created,
      tackles: b.tackles,
      yellow_cards: b.yellow_cards,
      red_cards: b.red_cards,
      penalty_misses: b.penalty_misses,
      own_goals: b.own_goals,
      saves: b.saves,
      penalty_saves: b.penalty_saves,
      clean_sheet: b.clean_sheet,
      goals_conceded: b.goals_conceded,
      bonus_points: b.bonus_points,
      ppms_earned: b.ppms_earned,
      ppm_successful_dribble: b.ppm_successful_dribble,
      ppm_accurate_cross: b.ppm_accurate_cross,
      ppm_interception: b.ppm_interception,
      ppm_block: b.ppm_block,
      ppm_goal_outside_area: b.ppm_goal_outside_area,
      ppm_foul_won: b.ppm_foul_won,
      ppm_offside: b.ppm_offside,
      ppm_foul_conceded: b.ppm_foul_conceded,
      ppm_error_leading_to_goal: b.ppm_error_leading_to_goal,
      ppm_successful_claim: b.ppm_successful_claim,
      ppm_successful_punch: b.ppm_successful_punch,
      ppm_keeper_sweep: b.ppm_keeper_sweep,
      ppm_pass_completion_90: b.ppm_pass_completion_90,
      ppm_pass_completion_80: b.ppm_pass_completion_80,
      ppm_pass_completion_70: b.ppm_pass_completion_70,
    },
    points: isTotalPoints
      ? (player.basicPoints ?? 0) + (player.ppmPoints ?? 0)
      : (player.basicPoints ?? 0),
    ppmPoints: player.ppmPoints ?? null,
    minutesPlayed: s.minutesPlayed ?? null,
    goalBonusNormalPoints: null,
    goalBonusCaptainPoints: null,
  };

  return matchdayStats;
};

export const buildMatchPlayerDetail = (
  player: Player,
  displayMatch: MatchData
): { playerMatches: PlayerMatch[]; matchdayStats: MatchdayStats } => {
  const playerMatch: PlayerMatch = {
    matchId: displayMatch.id,
    competitionLabel: displayMatch.competition,
    kickoffAt: displayMatch.kickoffAt,
    venue: displayMatch.venue || null,
    status: displayMatch.hasStarted
      ? displayMatch.statusVariant === 'live'
        ? 'playing'
        : 'played'
      : 'fixture',
    homeTeam: {
      name: displayMatch.homeTeam.name,
      shortName: null,
      flagKey: displayMatch.homeTeam.flag ?? displayMatch.homeTeam.code,
      score: String(displayMatch.homeTeam.score),
    },
    awayTeam: {
      name: displayMatch.awayTeam.name,
      shortName: null,
      flagKey: displayMatch.awayTeam.flag ?? displayMatch.awayTeam.code,
      score: String(displayMatch.awayTeam.score),
    },
    stats: buildMatchStat(player),
  };

  return { playerMatches: [playerMatch], matchdayStats: buildMatchStat(player, true) };
};
